/** @jsxImportSource @emotion/react */
import { forwardRef, memo } from 'react'
import { PureButtonWrapper } from './styles'
import { CardColor } from '@signal/components/PureCard'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import { useDarkModeContext } from '@signal/theme/Context/dark-mode'
import {
  COMMON_HEIGHTS,
  COMMON_SIZES,
  DEFAULT_SIZE
} from '@signal/theme/variables/sizes'

export interface PureButtonProps {
  children: React.ReactNode
  onClick?: () => void
  $color?: CardColor
  $disabled?: boolean
  $fullHeight?: boolean
  $circle?: boolean
  as?: 'button' | 'a'
  href?: string
  target?: string
  rel?: string
  $height?: COMMON_SIZES
  $size?: COMMON_SIZES
  $lighten?: boolean
  style?: React.CSSProperties
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>
}

export const BUTTON_FONT_SIZES = {
  [COMMON_SIZES.XSMALL]: 14,
  [COMMON_SIZES.SMALL]: 16,
  [COMMON_SIZES.MEDIUM]: 16,
  [COMMON_SIZES.LARGE]: 16
}

const PureButton = forwardRef<HTMLButtonElement, PureButtonProps>(
  (
    {
      children,
      onClick,
      $color,
      $disabled,
      $fullHeight,
      $circle,
      as,
      href,
      target,
      rel,
      $height = DEFAULT_SIZE,
      $lighten,
      onMouseEnter,
      onMouseLeave,
      $size = DEFAULT_SIZE,
      ...props
    },
    ref
  ) => {
    const { isDarkMode } = useDarkModeContext()

    const height = $size ? COMMON_HEIGHTS[$size] : COMMON_HEIGHTS[$height]
    const fontSize = $size
      ? BUTTON_FONT_SIZES[$size]
      : BUTTON_FONT_SIZES[$height]

    return (
      <PureButtonWrapper
        ref={ref}
        as={as}
        href={href}
        $fontSize={fontSize}
        target={target}
        rel={rel}
        onClick={$disabled ? undefined : onClick}
        $color={$color}
        $disabled={$disabled}
        disabled={onMouseEnter === undefined ? $disabled : false}
        $fullHeight={$fullHeight}
        $isDarkMode={isDarkMode}
        $circle={$circle}
        $height={height}
        $lighten={$lighten}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...props}
      >
        {children}
      </PureButtonWrapper>
    )
  }
)

PureButton.displayName = 'PureButton'

export default memo(withCommonSnapshotsOnly(PureButton))
